// WHAT: Settings view component for ChatGPT/OpenAI API configuration
// WHY:  Provides interface for managing OpenAI API credentials and model settings for commit message generation
// DOMAIN: Settings → ChatGPT
// DEPENDENCIES: Tauri core API, secure storage for API key
// RISKS:
//   - WARN: API key is displayed in plain text input when editing
//   - NOTE: Configuration allows saving without API key if already configured (for enabling/disabling)
//   - WARN: Connection test may expose API key validation failures

import { core } from "@tauri-apps/api";
import {
  MessageSquare,
  Key,
  Bot,
  Settings,
  TestTube,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Shield,
} from "lucide-react";
import React, { useState, useEffect } from "react";

import {
  Card,
  Button,
  Input,
  Label,
  Select,
  Slider,
  Switch,
  Stack,
  Heading,
  Badge,
  StatusIndicator,
} from "@/components/ui";

/**
 * WHAT:
 *   Interface defining ChatGPT configuration structure.
 *
 * WHY:
 *   Type-safe definition for configuration state and API responses.
 */
interface ChatGPTConfig {
  apiKey: string;
  enabled: boolean;
  maxTokens: number;
  model: string;
  temperature: number;
}

/**
 * WHAT:
 *   React component for managing ChatGPT/OpenAI API configuration.
 *
 * WHY:
 *   Enables users to configure OpenAI API credentials and model settings for
 *   intelligent commit message generation and AI-powered features.
 *
 * BEHAVIOR:
 *   - Loads configuration from secure storage on mount
 *   - Supports edit mode for updating credentials
 *   - Allows enabling/disabling integration without re-entering API key
 *   - Provides connection test functionality
 *   - Saves configuration to secure storage
 *   - Displays current settings and status
 *
 * RISKS:
 *   - WARN: API key input uses type="password" but may still be visible in some contexts
 *   - NOTE: Configuration can be saved without API key if already configured (for toggle)
 *   - WARN: Connection test validates API key but may expose validation errors
 *
 * @returns {JSX.Element} ChatGPT settings view component
 *
 */
export const ChatGPTSettingsView: React.FC = () => {
  const [config, setConfig] = useState<ChatGPTConfig>({
    apiKey: "",
    enabled: false,
    maxTokens: 150,
    model: "gpt-3.5-turbo",
    temperature: 0.3,
  });
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [success, setSuccess] = useState<null | string>(null);

  const models = [
    { label: "GPT-4 (Most Capable)", value: "gpt-4" },
    { label: "GPT-4 Turbo (Fast & Capable)", value: "gpt-4-turbo" },
    { label: "GPT-3.5 Turbo (Fast & Efficient)", value: "gpt-3.5-turbo" },
  ];

  useEffect(() => {
    void loadConfig();
  }, []);

  /**
   * WHAT:
   *   Loads ChatGPT configuration from secure storage.
   *
   * WHY:
   *   Retrieves existing configuration to populate form and determine configuration status.
   *
   * SIDE EFFECTS:
   *   - Fetches configuration from backend via Tauri command
   *   - Updates configuration state (excluding API key for security)
   *   - Updates configuration status flag
   *
   * RISKS:
   *   - NOTE: API key is intentionally not loaded into state for security
   *   - WARN: Fails silently if configuration cannot be loaded
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      const result = await core.invoke<ChatGPTConfig>("get_chatgpt_config");
      if (result.apiKey) {
        setIsConfigured(true);
        // NOTE: Don't set the actual API key for security - only load other settings
        setConfig((previous) => ({
          ...previous,
          enabled: result.enabled || previous.enabled,
          maxTokens: result.maxTokens || previous.maxTokens,
          model: result.model || previous.model,
          temperature: result.temperature || previous.temperature,
        }));
      } else {
        setIsConfigured(false);
      }
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to load configuration");
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * WHAT:
   *   Saves ChatGPT configuration to secure storage.
   *
   * WHY:
   *   Persists configuration changes including API key, model settings, and enabled status.
   *
   * SIDE EFFECTS:
   *   - Loads existing API key if not provided in form (for enable/disable toggle)
   *   - Saves configuration to secure storage
   *   - Clears API key from form after save
   *   - Reloads configuration to get updated status
   *   - Updates configuration status
   *
   * RISKS:
   *   - NOTE: Allows saving without API key if already configured (for enable/disable)
   *   - WARN: API key is loaded from backend if not in form state - may fail if backend unavailable
   *   - NOTE: Form API key is cleared after save for security
   */
  /**
   * WHAT:
   *   Loads existing API key from backend if not present in form state.
   *
   * WHY:
   *   Allows enabling/disabling integration without re-entering API key.
   *
   * @returns {Promise<string | undefined>} API key from backend or undefined
   *
   */
  const loadExistingApiKey = async (): Promise<string | undefined> => {
    if (!isConfigured || config.apiKey) {
      return undefined;
    }

    const existingConfig = await core.invoke<ChatGPTConfig>("get_chatgpt_config");
    return existingConfig.apiKey;
  };

  const saveConfig = async () => {
    if (!config.apiKey && !isConfigured) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const existingApiKey = await loadExistingApiKey();
      const configToSave = {
        ...config,
        apiKey: existingApiKey ?? config.apiKey,
      };

      await core.invoke("save_chatgpt_config", { config: configToSave });
      setSuccess("Configuration saved successfully!");
      setIsConfigured(true);
      setIsEditing(false);

      if (config.apiKey) {
        setConfig((previous) => ({ ...previous, apiKey: "" }));
      }

      await loadConfig();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  /**
   * WHAT:
   *   Tests ChatGPT API connection with current configuration.
   *
   * WHY:
   *   Validates API key and configuration before saving.
   *
   * SIDE EFFECTS:
   *   - Invokes connection test command with current config
   *   - Updates success/error state based on result
   *   - Displays validation feedback
   *
   * RISKS:
   *   - WARN: Connection test may expose API key validation failures
   *   - NOTE: Uses current form config, not saved config
   *   - WARN: Error messages may reveal sensitive information about API key issues
   */
  const testConnection = async () => {
    if (!config.apiKey) {
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setSuccess(null);

      const result = await core.invoke<{ success: boolean; message: string }>(
        "test_chatgpt_connection",
        {
          config,
        },
      );

      if (result.success) {
        setSuccess("✅ Connection successful! API key is valid.");
      } else {
        setError(`❌ Connection failed: ${result.message}`);
      }
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to test connection");
    } finally {
      setTesting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setConfig((previous) => ({ ...previous, apiKey: "" }));
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (field: keyof ChatGPTConfig, value: boolean | number | string) => {
    setConfig((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const renderConfigurationForm = () => {
    if (isConfigured && !isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Settings className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              {isConfigured ? "Update ChatGPT Configuration" : "Configure ChatGPT Integration"}
            </Heading>
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              <span>OpenAI API Key</span>
              <span className="text-red-400">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              onChange={(e) => {
                handleInputChange("apiKey", e.target.value);
              }}
              placeholder="sk-..."
              size="md"
              type="password"
              value={config.apiKey}
            />
            <p className="text-slate-500 text-xs">
              Your API key is stored securely in Tauri&apos;s secure storage.
            </p>
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <span>Model</span>
            </Label>
            <Select
              className="w-full"
              onChange={(value) => {
                const modelValue = Array.isArray(value) ? value[0] : value;
                if (typeof modelValue === "string") {
                  handleInputChange("model", modelValue);
                }
              }}
              options={models}
              placeholder="Select a model"
              size="md"
              value={config.model}
            />
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300">Max Tokens: {config.maxTokens}</Label>
            <Slider
              className="w-full"
              max={300}
              min={50}
              onChange={(value) => {
                handleInputChange("maxTokens", value);
              }}
              step={10}
              value={config.maxTokens}
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>50</span>
              <span>150</span>
              <span>300</span>
            </div>
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300">Temperature: {config.temperature}</Label>
            <Slider
              className="w-full"
              max={1}
              min={0}
              onChange={(value) => {
                handleInputChange("temperature", value);
              }}
              step={0.1}
              value={config.temperature}
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>Focused (0.0)</span>
              <span>Balanced (0.5)</span>
              <span>Creative (1.0)</span>
            </div>
          </Stack>

          <Stack align="center" direction="row" gap={3}>
            <Switch
              checked={config.enabled}
              disabled={loading || saving}
              onChange={(checked) => {
                handleInputChange("enabled", checked);
              }}
            />
            <Label className="text-slate-300">Enable ChatGPT integration</Label>
          </Stack>

          <Stack className="flex-wrap" direction="row" gap={2}>
            <Button
              disabled={loading || saving || testing || !config.apiKey}
              onClick={() => {
                void testConnection();
              }}
              size="md"
              variant="secondary"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube className="w-4 h-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>

            <Button
              disabled={loading || saving || (!config.apiKey && !isConfigured)}
              onClick={() => {
                void saveConfig();
              }}
              size="md"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isConfigured ? "Update Configuration" : "Save Configuration"}
                </>
              )}
            </Button>

            {isEditing ? (
              <Button disabled={saving} onClick={handleCancel} size="md" variant="ghost">
                Cancel
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    );
  };

  const renderConfiguredState = () => {
    if (!isConfigured || isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                ChatGPT Configured
              </Heading>
            </Stack>

            <Button onClick={handleEdit} size="md" variant="ghost">
              <Settings className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Stack>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Model</Label>
              <Badge size="sm" style="subtle" variant="default">
                {config.model}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Max Tokens</Label>
              <Badge size="sm" style="subtle" variant="default">
                {config.maxTokens}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Temperature</Label>
              <Badge size="sm" style="subtle" variant="default">
                {config.temperature}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Status</Label>
              <Badge size="sm" style="subtle" variant={config.enabled ? "success" : "warning"}>
                {config.enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>

          <Stack
            align="center"
            className="pt-4 border-t border-slate-700"
            direction="row"
            justify="between"
          >
            <Stack align="center" direction="row" gap={3}>
              <Switch
                checked={config.enabled}
                disabled={loading || saving}
                onChange={(checked) => {
                  handleInputChange("enabled", checked);
                  setTimeout(() => {
                    void saveConfig();
                  }, 100);
                }}
              />
              <Label className="text-slate-300">Enable ChatGPT integration</Label>
            </Stack>
            {saving ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin" /> : null}
          </Stack>

          <Stack direction="col" gap={2}>
            <p className="text-slate-300 text-sm">
              Your ChatGPT integration is configured and ready to use for intelligent commit message
              generation.
            </p>
            <p className="text-slate-500 text-xs">
              The API key is securely stored and will be used for all ChatGPT-powered features.
            </p>
          </Stack>
        </Stack>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <MessageSquare className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              ChatGPT Integration
            </Heading>
            <p className="text-slate-300">
              Configure OpenAI API settings for intelligent commit message generation and AI-powered
              features.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Key className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <StatusIndicator
          description="Your OpenAI API key for ChatGPT integration"
          isConfigured={isConfigured}
          label="OpenAI API Key"
          showLock
          size="md"
        />
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">How it works</h3>
            <ul className="text-sm text-blue-300 space-y-1">
              <li>• Your bullet points are sent to OpenAI&apos;s API</li>
              <li>• ChatGPT generates professional commit messages</li>
              <li>• Messages follow Conventional Commits format</li>
              <li>• API key is stored securely in Tauri&apos;s secure storage</li>
              <li>• No data is logged or stored by us</li>
            </ul>
          </div>
        </Stack>
      </Card>

      {renderConfigurationForm()}
      {renderConfiguredState()}

      {/* Status Messages */}
      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Loading State */}
      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading ChatGPT configuration...</span>
          </Stack>
        </Card>
      ) : null}

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Privacy</h3>
            <p className="text-sm text-green-300">
              Your OpenAI API key is stored securely in Tauri&apos;s encrypted storage. No sensitive
              data is ever displayed in the frontend, and all API calls are made directly from the
              secure backend.
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
};

// Named export already defined above
